import React, { useState } from 'react';

import { openBlob } from '@apis/common';
import { pdfApis } from '@apis/next/pdfApis';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { Layout } from '@components/Layout';
import { BookingInquiryDisplay } from '@components/utilities/BookingInquiry/BookingInquiryDisplay';
import { BookingInquiryShareDialog } from '@components/utilities/Bookings/BookingInquiryShareDialog';
import { BookingsContainer } from '@components/utilities/Bookings/BookingsContainer';
import { CancelBookingInquiryDialog } from '@components/utilities/Bookings/CancelBookingInquiryDialog';
import { UnsharableDialog } from '@components/utilities/Bookings/UnsharableDialog';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { bookingsStrings } from '@data/bookings';
import { useBookingInquiryCancelDetail } from '@hooks/mutation/useBookingInquiryCancelDetail';
import { useBookingInquiryDetail } from '@hooks/queries/useBookingInquiryDetail';
import { useUser } from '@hooks/queries/useUser';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import { useSearch } from '@hooks/useSearch';
import Grid from '@material-ui/core/Grid';
import { clientInquiryStatusMap, InquiryDetail } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime, toJST } from '@utils/types';
import clsx from 'clsx';
import * as t from 'io-ts';

const useStyles = makeMuiStyles((theme, common) => ({
  ddText: {
    ...common.textGray900,
    marginLeft: '1rem',
  },
  inquiryTitle: {
    ...common.middleText,
    ...common.textGray600,
  },
  additionalData: {
    ...common.middleText,
    margin: '1rem 0 2rem 1rem',
    fontWeight: 500,
  },
  inquiryFooter: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.middleText,
    ...common.textGray800,
    marginTop: '2rem',
  },
}));

const strings = createStrings({
  ja: {
    inquiry: '予約状況',
    inquiryContents: '予約内容確認',
    d2dNo: 'D2D番号',
    createdAt: '送信時間',
    author: '作成者',
    search: '絞り込み',
    no: 'No',
    transmissionTime: '送信時間',
    status: 'ステータス',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    confirming: '確認中',
    canceled: 'キャンセル済み',
    bookingConfirmed: '予約確定',
    details: '詳細',
    fileDownload: '添付資料',
    cancelInquiry: '予約をキャンセル',
    shareInquiry: '予約を共有する',
    customerNo: 'お客様の管理番号',
    changeLog: '変更履歴',
    contactD2d: 'D2Dに連絡する',
    sorryYouCannotShare:
      '申し訳ありませんが、予約を共有する権限がありません。予約の作成者のみが予約を共有できます。',
  },
});

const Inquiry: React.FC<{
  bookingInquiry: InquiryDetail;
}> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { bookingInquiry } = props;
  const user = useUser();

  const isCreator = !!bookingInquiry.can_views.find(
    (canView) => canView.user === user.data?.id && canView.can_view_type === 'creator',
  );

  const onContact = () => {
    window.open('mailto:d2d@fcstandard.com', '_blank');
  };

  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const [unSharableDialogOpen, setUnSharableDialogOpen] = useState(false);

  return (
    <div className={clsx(classes.flexInline)}>
      <UnsharableDialog
        open={unSharableDialogOpen}
        onClose={() => setUnSharableDialogOpen(false)}
      />
      <Grid
        container
        spacing={3}
        className={clsx(classes.middleText, classes.textGray500)}
        style={{ marginTop: 8 }}
      >
        <Grid item xs={12} sm={4}>
          <span>{strings.d2dNo}</span>
        </Grid>
        <Grid item xs={12} sm={8}>
          <span className={classes.ddText}>{bookingInquiry.d2d_no}</span>
        </Grid>
        <Grid item xs={12} sm={4}>
          <span>{strings.createdAt}</span>
        </Grid>
        <Grid item xs={12} sm={8}>
          <span className={classes.ddText}>
            {bookingInquiry.created_at ? toJST(new Date(bookingInquiry.created_at)) : '-'}
          </span>
        </Grid>
        <Grid item xs={12} sm={4}>
          <span>{strings.customerNo}</span>
        </Grid>
        <Grid item xs={12} sm={8}>
          <span className={classes.ddText}>{bookingInquiry.customer_no}</span>
        </Grid>
        <Grid item xs={12} sm={4}>
          <span>{strings.author}</span>
        </Grid>
        <Grid item xs={12} sm={8}>
          <span
            className={classes.ddText}
          >{`${bookingInquiry.user?.last_name} ${bookingInquiry.user?.first_name}`}</span>
        </Grid>
        <Grid item xs={12} sm={4}>
          <span>{strings.status}</span>
        </Grid>
        <Grid item xs={12} sm={8}>
          <span className={classes.ddText}>
            {clientInquiryStatusMap(bookingInquiry.status || '-')}
          </span>
        </Grid>
      </Grid>
      <div
        className={clsx(classes.flexColumn, classes.verticalSpace1)}
        style={{ width: 136, flexShrink: 0 }}
      >
        <Button color="primary" size="small" text={strings.contactD2d} onClick={onContact} />

        <Button
          color={isCreator ? 'accent' : 'grey'}
          size="small"
          text={strings.shareInquiry}
          onClick={() => {
            isCreator ? setShareDialogOpen(true) : setUnSharableDialogOpen(true);
          }}
        />
      </div>
      <BookingInquiryShareDialog
        inquiry={bookingInquiry}
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
      />
    </div>
  );
};

const searchType = t.type({
  d2dNo: t.string,
});

export const BookingInquiryHistoryDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { d2dNo },
  } = useSearch(searchType);

  const user = useUser();
  const bookingInquiryCancelDetail = useBookingInquiryCancelDetail(d2dNo);

  const { data, isLoading, error } = useBookingInquiryDetail(d2dNo);

  const downloadBookingInquiryDisplayPdf = useMutation(() =>
    pdfApis.client.get('/pdf/BookingInquiryDisplay/:d2d_no', { params: { d2d_no: d2dNo } }),
  );

  const downloadBookingInquiryPriceSettlement = useMutation(() =>
    pdfApis.client.get('/pdf/BookingInquiryPriceSettlement/:d2d_no', { params: { d2d_no: d2dNo } }),
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [downloadBookingInquiryDisplayPdf, downloadBookingInquiryPriceSettlement],
  });

  const isCreator = !!data?.can_views.find(
    (canView) => canView.user === user.data?.id && canView.can_view_type === 'creator',
  );

  const [UIState, setUIState] = useState(() => ({
    cancelBookingInquiryDialog: {
      open: false,
    },
  }));

  return (
    <Layout
      loading={isLoading || user.isLoading}
      title={bookingsStrings.bookingInquiryHistory}
      errors={[error, user.error]}
      breadcrumbs={[
        '/utilities/Dashboard',
        '/utilities/Bookings/BookingInquiryHistory',
        {
          text: d2dNo as string,
        },
      ]}
    >
      <CancelBookingInquiryDialog
        open={UIState.cancelBookingInquiryDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, cancelBookingInquiryDialog: { open: false } };
          });
        }}
        inquiry={{
          number: d2dNo as string,
          transmissionTime: data?.created_at == null ? '-' : formatDateTime(data?.created_at),
          author: data?.user == null ? '-' : `${data.user?.last_name} ${data.user?.first_name}`,
          status: clientInquiryStatusMap(data?.status ?? ''),
        }}
        onSubmit={async (values) => {
          const cancelledReason = values.cancelledReason ? `\n${values.cancelledReason}` : '';
          await bookingInquiryCancelDetail.mutateAsync({
            cancel_detail: `${values.reasonType}${cancelledReason}`,
          });
        }}
      />
      <BookingsContainer>
        <div>
          <FormTitle
            title={strings.inquiry}
            actions={
              <>
                <Button
                  color="primary"
                  size="small"
                  text={strings.cancelInquiry}
                  onClick={() => {
                    setUIState((state) => {
                      return { ...state, cancelBookingInquiryDialog: { open: true } };
                    });
                  }}
                  disabled={
                    ['客側キャンセル済', 'FCキャンセル済'].includes(data?.status ?? '') ||
                    !isCreator
                  }
                />
              </>
            }
          />
          <Inquiry bookingInquiry={data!} />
        </div>

        <VerticalSpace height={16} />

        <div>
          <FormTitle
            title={strings.inquiryContents}
            actions={
              <div className={clsx(classes.flexInline, classes.horizontalSpace1)}>
                <Button
                  color="primary"
                  text={bookingInquiryStrings.printBookingInquiryDisplay}
                  size="small"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const blob = await downloadBookingInquiryDisplayPdf.mutateAsync();
                    openBlob(
                      blob,
                      `${bookingInquiryStrings.printBookingInquiryDisplay}.pdf`,
                      'application/pdf',
                    );
                  }}
                  type="button"
                />

                <Button
                  color="primary"
                  text={bookingInquiryStrings.printBookingInquiryPriceSettlement}
                  size="small"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const blob = await downloadBookingInquiryPriceSettlement.mutateAsync();
                    openBlob(
                      blob,
                      `${bookingInquiryStrings.printBookingInquiryPriceSettlement}.pdf`,
                      'application/pdf',
                    );
                  }}
                  type="button"
                />
              </div>
            }
          />
          {renderAlertsAndSpinnerModal()}

          <BookingInquiryDisplay bookingInquiry={data!} variant="content" type="client" />
          <div className={classes.inquiryFooter}>
            <span>
              Lastest Update: {(data?.updated_at && toJST(new Date(data.updated_at))) || '-'}
            </span>

            <span
              className={clsx(classes.link, classes.baseButton)}
              onClick={() =>
                window.open(`${url('/utilities/Bookings/BookingInquiryLogs')}?d2d_no=${d2dNo}`)
              }
            >
              {strings.changeLog}
            </span>
          </div>
        </div>
      </BookingsContainer>
    </Layout>
  );
};
